import React, { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Target,
  User,
  Paperclip,
  X,
  Check,
  FileText,
} from "lucide-react";
import { showErrorToast } from "../../../components/ConfirmDialog";
import { validatePlanPdfFile } from "./planFileConstraints";

/**
 * Modal thêm / sửa một mục tiêu trong form tạo kế hoạch (PlanCreateModal).
 * Field: goal, owner, attachment (File hoặc giữ nguyên khi sửa).
 *
 * @param {string} [planCategory] — branch | department | personal
 * @param {string} [defaultExecutorName] — tên hiển thị tài khoản đăng nhập (mặc định người thực hiện)
 */
const PlanDetailModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  planCategory = "",
  defaultExecutorName = "",
}) => {
  const [goal, setGoal] = useState("");
  const [owner, setOwner] = useState("");
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);

  const isPersonal = (planCategory || "").toLowerCase() === "personal";
  const isEdit = Boolean(initialData?.id);
  const existingFileLabel =
    initialData?.attachment && !(initialData.attachment instanceof File)
      ? typeof initialData.attachment === "string"
        ? initialData.attachment
        : initialData.attachment?.name || "—"
      : initialData?.attachment instanceof File
        ? initialData.attachment.name
        : null;

  useEffect(() => {
    if (!isOpen) return;
    setGoal(initialData?.goal || "");
    const fallback = (defaultExecutorName || "").trim();
    if (isPersonal) {
      setOwner(fallback);
    } else {
      const fromItem = (initialData?.owner || "").trim();
      setOwner(fromItem || fallback);
    }
    setAttachment(initialData?.attachment instanceof File ? initialData.attachment : null);
  }, [isOpen, initialData, isPersonal, defaultExecutorName]);

  const handleBackdropClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const onAttachmentPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setAttachment(null);
      return;
    }
    const v = validatePlanPdfFile(f);
    if (!v.ok) {
      showErrorToast(v.message || "Tệp không hợp lệ.");
      e.target.value = "";
      return;
    }
    setAttachment(f);
  };

  const handleSubmit = () => {
    if (!goal.trim()) {
      showErrorToast("Vui lòng nhập mục tiêu.");
      return;
    }
    const fallback = (defaultExecutorName || "").trim();
    const ownerTrim = isPersonal ? fallback : owner.trim();
    if (!ownerTrim) {
      showErrorToast(
        isPersonal
          ? "Không xác định được tên tài khoản đăng nhập cho người thực hiện."
          : "Vui lòng nhập người thực hiện."
      );
      return;
    }
    if (attachment instanceof File) {
      const v = validatePlanPdfFile(attachment);
      if (!v.ok) {
        showErrorToast(v.message || "Tệp đính kèm không hợp lệ.");
        return;
      }
    }
    onSave?.({
      id: initialData?.id,
      goal: goal.trim(),
      owner: ownerTrim,
      attachment: attachment || initialData?.attachment || null,
    });
    onClose?.();
  };

  const fieldClass =
    "w-full rounded-2xl border-2 border-transparent bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-red-500 focus:bg-white focus:ring-0";

  const ownerLockedClass = isPersonal
    ? "cursor-not-allowed border-gray-100 bg-gray-100/80 text-gray-700"
    : "";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="plan-detail-modal-title"
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.button
            type="button"
            aria-label="Đóng"
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleBackdropClose}
          />

          <motion.div
            className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl shadow-gray-200/50"
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", damping: 26, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 bg-gradient-to-r from-red-50/80 to-white px-5 py-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-700 text-white shadow-lg shadow-red-200">
                  <Target className="h-6 w-6" strokeWidth={2.25} aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3
                    id="plan-detail-modal-title"
                    className="text-base font-black uppercase tracking-tight text-gray-900 sm:text-lg"
                  >
                    {isEdit ? "Sửa mục tiêu" : "Thêm mục tiêu"}
                  </h3>
                  <p className="mt-0.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Chi tiết kế hoạch · HRM
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                aria-label="Đóng"
              >
                <X size={20} strokeWidth={2.25} />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[min(70vh,520px)] overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
              <div className="space-y-5">
                <div>
                  <label className="mb-2 ml-0.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                    <Target className="h-3.5 w-3.5 text-red-500" strokeWidth={2.5} />
                    Mục tiêu <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="Mô tả mục tiêu cụ thể, có thể đo lường…"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className="mb-2 ml-0.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                    <User className="h-3.5 w-3.5 text-red-500" strokeWidth={2.5} />
                    Người thực hiện <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={owner}
                    onChange={(e) => setOwner(e.target.value)}
                    readOnly={isPersonal}
                    placeholder="Họ tên người phụ trách"
                    className={`${fieldClass} ${ownerLockedClass}`}
                  />
                  <p className="mt-1.5 text-[11px] font-medium leading-snug text-gray-500">
                    {isPersonal
                      ? "Kế hoạch phân loại «Cá nhân»: người thực hiện luôn là tài khoản đăng nhập."
                      : "Mặc định theo tài khoản đăng nhập; có thể chỉnh khi phân loại không phải Cá nhân."}
                  </p>
                </div>

                <div>
                  <label className="mb-2 ml-0.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                    <Paperclip className="h-3.5 w-3.5 text-red-500" strokeWidth={2.5} />
                    Tài liệu đính kèm
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="sr-only"
                    onChange={onAttachmentPick}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="group flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center transition hover:border-red-300 hover:bg-red-50/40"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition group-hover:border-red-200 group-hover:text-red-600">
                      <Paperclip size={20} strokeWidth={2.25} />
                    </div>
                    <span className="text-sm font-bold text-gray-700">
                      {attachment
                        ? attachment.name
                        : "Chọn tệp PDF (tối đa 25 MB)"}
                    </span>
                    <span className="text-xs font-medium text-gray-500">
                      Chỉ PDF · tệp mới sẽ thay thế tệp cũ khi gửi biểu mẫu
                    </span>
                  </button>

                  {initialData?.attachment && !attachment && existingFileLabel && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2.5 text-xs text-amber-900">
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={2.25} />
                      <div>
                        <span className="font-bold">Đang giữ tệp hiện tại:</span>{" "}
                        <span className="break-all opacity-90">{existingFileLabel}</span>
                        <span className="mt-1 block text-[11px] font-medium text-amber-800/90">
                          Chọn PDF mới ở trên nếu cần thay đổi.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/90 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] rounded-2xl border-2 border-gray-200 bg-white px-5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 active:scale-[0.98]"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-gradient-to-r from-red-700 to-red-800 px-6 text-sm font-black uppercase tracking-wide text-white shadow-md shadow-red-100 transition hover:shadow-lg active:scale-[0.98]"
              >
                <Check size={18} strokeWidth={2.5} className="shrink-0" />
                Lưu mục tiêu
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlanDetailModal;
